import packageInfo from '../../../../package.json';
import
{
    env
} from '../../Environment';

import
{
    useL10n
} from "@ews/react-localization-context";

import
{
    IonButton,
    IonInput,
    IonItem,
    IonLabel,
    IonSelect,
    IonSelectOption,
    useIonRouter
} from "@ionic/react";

import
{
    power
} from "ionicons/icons";

import
{
    FC, FormEvent, ReactElement, useCallback, useEffect, useState
} from "react";

import
{
    MenuItem,
    MenuLink,
    MenuSpacer,
    Sidebar,
    SidebarSection,
} from "./Sidebar";

import
{
    MenuToggleProps
} from "../types";

import
{
    useNamedRoutes
} from "../../NamedRoutes";

import
{
    AuthorizationLevel
} from "@ews/zlt-events";

import
{
    useAuthorization,
    usePermitAuthorization
} from "../../Authorization";

import
{
    ReactClickEvent
} from "../../types";

import
{
    useAuth,
    useImpersonationStateWithSetter,
    useMyPermissions,
    useMyProfileId,
    useMyProfileWithRefresh,
} from "../../Login/LoginProvider";

import
{
    LicenseType,
    UserPermissions,
    permissionAllowed
} from "@ews/react-data";

import
{
    localeTranslationKeys
} from '../../../setup/locale';

import
{
    useAlert
} from '../../Dialog';

import
{
    useHasLicense
} from '../../HasLicense';

import
{
    LegalModalContent,
    useModal
} from '../../Modal/useModal';

import Icon from '@ews/react-icons';
import style from './Main.module.scss';

type MenuToggleEvent = ReactClickEvent;

export type MainSidebarProps = MenuToggleProps & {
    children?: ReactElement;
};

export type AcquireAuthorizationProps = MenuToggleProps & {
    systemId: string;
};

export const AcquireAuthorization: FC<AcquireAuthorizationProps> = ({
    onMenuToggle,
    systemId
}) =>
{
    const { authorize, authorizationLevel } = useAuthorization();
    const authorizationPermitted = usePermitAuthorization(systemId);
    const useHasLicenseOperateAndPushLicense = useHasLicense(LicenseType.OPERATE_AND_NOTIFICATION);

    const [code2, setCode2] = useState("");
    const [code3, setCode3] = useState("");

    const alert = useAlert();

    const { translate: t } = useL10n();
    const permissions: UserPermissions = useMyPermissions();

    const showMessage = async (message: string) =>
    {
        await alert({
            "title": t("[REACT_AUTHORIZATION_FAILED]"),
            "message": t(message),
            "buttonTitle": t('COM_BANNERS_CANCEL'),
            cssClass: style.cancelButton,
            buttonId: "cancel"
        });
    };

    const authorizeWrapper = async (e: MenuToggleEvent, level: AuthorizationLevel, code?: string) =>
    {
        try {
            const result = await authorize(level, code);

            if (result === true) {

                onMenuToggle && onMenuToggle(e);
                return true;
            } else {

                if (result) {

                    if (result.phoneNumber) {
                        showMessage(t('[REACT_AUTHORIZATION_BLOCKED_PHONE_MESSAGE]', result));
                    } else {
                        showMessage(t('[REACT_AUTHORIZATION_BLOCKED_MESSAGE]', result));
                    }
                }
            }

        } catch (e) {
            showMessage(t(e as string));
        }
    };

    const handleForm = async (e: FormEvent, authorizationLevel: AuthorizationLevel) =>
    {
        e.preventDefault();

        const code = authorizationLevel === AuthorizationLevel.LEVEL2 ? code2 : code3;
        await authorizeWrapper(e as unknown as MenuToggleEvent, authorizationLevel, `${code}`);

        setCode2("");
        setCode3("");
    };



    if (!useHasLicenseOperateAndPushLicense) return <></>;

    if (!authorizationPermitted) return <></>;

    if (authorizationLevel > AuthorizationLevel.LEVEL1) {
        return (
            <IonItem detail={false} onClick={(e) => authorizeWrapper(e, AuthorizationLevel.LEVEL1)} button={true} >
                <IonLabel>{t('[REACT_EXIT_AUTHORIZATION]')}</IonLabel>
                <Icon icon={`lock_icon_locked`} />
            </IonItem>
        );
    }

    return (
        <>
            {permissionAllowed(permissions.permitAccessLevel2!) ? <form onSubmit={(e) => handleForm(e, AuthorizationLevel.LEVEL2)}><MenuItem>
                <IonLabel position="stacked">{t('[REACT_AUTHORIZATION_LEVEL_DYNAMIC]', { authorizationLevel: '2' })}</IonLabel>
                <IonInput value={code2} onIonInput={(e) => setCode2(e.detail.value || "")} label="" placeholder={t('[REACT_ENTER_CODE]')} />
                <IonButton type='submit' color={'light'} slot="end" ><Icon icon={`lock_icon_unlocked2`} /></IonButton>
            </MenuItem ></form> : <></>
            }

            {permissionAllowed(permissions.permitAccessLevel3!) ? <form onSubmit={(e) => handleForm(e, AuthorizationLevel.LEVEL3)}><MenuItem>
                <IonLabel position="stacked">{t('[REACT_AUTHORIZATION_LEVEL_DYNAMIC]', { authorizationLevel: '3' })}</IonLabel>
                <IonInput value={code3} onIonInput={(e) => setCode3(e.detail.value || "")} label="" placeholder={t('[REACT_ENTER_CODE]')} />
                <IonButton type='submit' color={'light'} slot="end"><Icon icon={`lock_icon_unlocked3`} /></IonButton>
            </MenuItem ></form> : <></>
            }

        </>
    );

};


const LanguageSelector: FC<{ lines?: HTMLIonItemElement["lines"]; }> = ({ lines }) =>
{
    const { translate: t, getLocale, getLocales } = useL10n();
    const routes = useNamedRoutes();
    const router = useIonRouter();

    const localeChange = (e: CustomEvent) =>
    {
        const locale = e.detail.value;
        router.push(routes.generate(null, { locale }));
    };

    return (
        <MenuItem lines={lines}>
            <IonSelect
                onIonChange={localeChange}
                value={getLocale()}
                interface="popover"
                style={{ width: "100%" }}
                placeholder={t('[REACT_SELECT_LANGUAGE]')}
            >
                {getLocales().map((locale) =>
                {
                    return (
                        <IonSelectOption
                            key={locale}
                            value={locale}
                        >
                            {t(localeTranslationKeys[locale])}
                        </IonSelectOption>
                    );
                })}

            </IonSelect>
        </MenuItem>
    );
};

const Main: FC<MainSidebarProps> = ({ onMenuToggle, children }) =>
{
    const { translate: t } = useL10n();
    const { generate, router } = useNamedRoutes();
    const { logout: _logout, refreshToken } = useAuth();
    const me = useMyProfileId();
    const { openModal, modal } = useModal();
    const permissions: UserPermissions = useMyPermissions();
    const [impersonationState, setImpersonationState, clearImpersonationState] = useImpersonationStateWithSetter();
    const [myProfile, refreshMyProfile] = useMyProfileWithRefresh();


    useEffect(() =>
    {
        if (!router) {
            console.error("Main Sidebar: useEffect redirect not possible because router is falsy!");
            return;
        }

        if (impersonationState.impersonationStatus === "ending" && myProfile.id === impersonationState.realUserId) {
            clearImpersonationState();
            const redirect = generate('user', { id: impersonationState.realUserId });
            // console.log("Main Sidebar: useEffect redirecting now:", redirect);
            router.push(redirect);
        }
    }, [myProfile, impersonationState, router, generate, clearImpersonationState]);

    const logout = useCallback(async () =>
    {
        if (impersonationState.impersonationStatus === "active") {
            try {
                // console.log("Main Sidebar: Instead of logging out we DE-PERSONATE via token refresh");
                await refreshToken(impersonationState.originalToken);
                await refreshMyProfile();

                setImpersonationState({
                    ...impersonationState,
                    impersonationStatus: "ending"
                });
                return;
            }
            catch (e) {
                console.error("Main Sidebar: Error: token refresh failed. Fallback to normal logout.");
            }
        }

        clearImpersonationState();
        _logout();
    }, [impersonationState, clearImpersonationState, _logout, refreshToken, refreshMyProfile, setImpersonationState]);

    return (
        <Sidebar title={t('COM_MENUS_HEADING_MENU')} onMenuToggle={onMenuToggle}>
            <SidebarSection grow>
                <LanguageSelector />
                <MenuLink
                    title={t('COM_ADMIN_VIEW_PROFILE_TITLE')}
                    onClick={onMenuToggle}
                    link={me ? generate('user', { id: me }) : ""}
                    icon={<Icon icon="user" />}
                />

            </SidebarSection>
            {children && <SidebarSection>{children}</SidebarSection>}
            <SidebarSection>
                {permissions.licenseManagement && <>
                    <MenuSpacer />
                    <MenuLink
                        title={t('COM_FACPMANAGEMENT_FACP_MANAGER_PRICES')}
                        onClick={onMenuToggle}
                        link={generate('priceLists', { id: me })}
                        icon={<Icon icon='list' />}
                    />
                    <MenuSpacer />
                </>}
                <MenuLink
                    title={t('[REACT_HELP]')}
                    onClick={onMenuToggle}
                    link={generate('home')}
                    icon={<Icon icon="questionmark" />}
                />
                <MenuLink
                    title={impersonationState.impersonationStatus === "active" ? t('[REACT_SIMULATION_HEADER_SIMULATE_END]') : t('[SID_DLG_HEADER_AUTHEND]')}
                    onClick={logout}
                    // onClick={onMenuToggle}
                    // link={generate('home')}
                    icon={power}
                />

                {/*                {token && <MenuLink
                    title={t('Dev Info')}
                    onClick={() => sendDeveloperInformation(token)}
                    />} */}
                <MenuItem >
                    <button
                        onClick={() => openModal(<LegalModalContent />)}
                        className={style.linkButton}
                    >
                        {t('[REACT_IMPRINT]')}
                    </button>
                </MenuItem>
                <MenuLink
                    title={t('[REACT_GUIDELINES]')}
                    link={generate('guide-lines')}
                />


                <MenuItem className={style.versionInfo}>
                    {env.APP_VERSION || packageInfo.version}
                </MenuItem>
            </SidebarSection>
            {modal}
        </Sidebar >
    );

};

export default Main;
export { LanguageSelector };
